/**
 * Grafty Accessibility System
 * WCAG 2.1 Level AA compliance utilities and hooks.
 *
 * Covers:
 *   - Focus management (skip links, focus trap, focus restoration)
 *   - ARIA live regions (status announcements for screen readers)
 *   - Keyboard navigation (roving tabindex, arrow key handlers)
 *   - Color contrast (all interactive elements ≥ 4.5:1)
 *   - Motion (respects prefers-reduced-motion)
 *   - Form accessibility (error association, required labeling)
 *   - useAccessibility hook (loads user preferences from API)
 */

import { useEffect, useRef, useCallback, useState, createContext, useContext, ReactNode } from 'react'
import type { MeasurementSystem } from './units'
import { applyServerLanguage } from './i18n'

// ── Types ─────────────────────────────────────────────────────────────────────

export interface AccessibilityPreferences {
  // One of the named themes defined in design-system.css:
  // mango|apple|orange|watermelon|palm|nursery|floral|farm|peach|taupe
  theme: string
  // Light or dark surface palette, independent of the accent `theme`.
  mode: 'light' | 'dark'
  reduceMotion: boolean
  fontSizeOffset: number // -1, 0, +1, +2
  highContrast: boolean
  focusVisibleAlways: boolean
  screenReaderVerbose: boolean
  measurementSystem: MeasurementSystem // imperial | metric — units shown across the app
}

const defaultPrefs: AccessibilityPreferences = {
  theme: 'mango',
  mode: 'dark',
  reduceMotion: false,
  fontSizeOffset: 0,
  highContrast: false,
  focusVisibleAlways: false,
  screenReaderVerbose: false,
  measurementSystem: 'imperial',
}

// AccessibilityPreferences (camelCase) → /me/preferences API fields (snake_case).
// updatePref must send the snake_case key or the backend allowlist drops it (so
// the pref would apply live but never persist).
const PREF_API_KEY: Record<keyof AccessibilityPreferences, string> = {
  theme: 'theme',
  mode: 'mode',
  reduceMotion: 'reduce_motion',
  fontSizeOffset: 'font_size_offset',
  highContrast: 'high_contrast',
  focusVisibleAlways: 'focus_visible_always',
  screenReaderVerbose: 'screen_reader_verbose',
  measurementSystem: 'measurement_system',
}

// ── Context ───────────────────────────────────────────────────────────────────

interface AccessibilityContextValue {
  prefs: AccessibilityPreferences
  updatePref: <K extends keyof AccessibilityPreferences>(key: K, value: AccessibilityPreferences[K]) => void
  announce: (message: string, priority?: 'polite' | 'assertive') => void
}

const AccessibilityContext = createContext<AccessibilityContextValue>({
  prefs: defaultPrefs,
  updatePref: () => {},
  announce: () => {},
})

// ── Provider ──────────────────────────────────────────────────────────────────

export function AccessibilityProvider({ children }: { children: ReactNode }) {
  const [prefs, setPrefs] = useState<AccessibilityPreferences>(defaultPrefs)
  const [politeMsg, setPoliteMsg] = useState('')
  const [assertiveMsg, setAssertiveMsg] = useState('')

  // Load preferences from API on mount
  useEffect(() => {
    const token = (() => { try { const r = localStorage.getItem('scion-auth'); return r ? (JSON.parse(r)?.state?.token ?? null) : null } catch { return null } })()
    if (!token) return
    fetch(`${import.meta.env.VITE_API_URL ?? 'http://localhost:8080'}/api/v1/me/preferences`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (data) setPrefs({
          theme: data.theme || 'mango',
          mode: data.mode === 'light' ? 'light' : 'dark',
          reduceMotion: data.reduce_motion ?? false,
          fontSizeOffset: data.font_size_offset ?? 0,
          highContrast: data.high_contrast ?? false,
          focusVisibleAlways: data.focus_visible_always ?? false,
          screenReaderVerbose: data.screen_reader_verbose ?? false,
          measurementSystem: (data.measurement_system as MeasurementSystem) || 'imperial',
        })
        // Sync the UI language to the user's saved preference (i18n).
        if (data?.language) applyServerLanguage(data.language)
      })
      .catch(() => {})
  }, [])

  // Also respect OS-level prefers-reduced-motion
  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)')
    if (mq.matches && !prefs.reduceMotion) {
      setPrefs(p => ({ ...p, reduceMotion: true }))
    }
    const handler = (e: MediaQueryListEvent) => setPrefs(p => ({ ...p, reduceMotion: e.matches }))
    mq.addEventListener('change', handler)
    return () => mq.removeEventListener('change', handler)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Apply preferences to document root
  useEffect(() => {
    const root = document.documentElement
    root.setAttribute('data-theme', prefs.theme)
    root.setAttribute('data-mode', prefs.mode)
    root.setAttribute('data-high-contrast', String(prefs.highContrast))
    root.style.setProperty('--font-size-offset', String(prefs.fontSizeOffset))
    if (prefs.reduceMotion) root.classList.add('reduce-motion')
    else root.classList.remove('reduce-motion')
    if (prefs.focusVisibleAlways) root.classList.add('focus-visible-always')
    else root.classList.remove('focus-visible-always')
  }, [prefs])

  const updatePref = useCallback(<K extends keyof AccessibilityPreferences>(
    key: K, value: AccessibilityPreferences[K]
  ) => {
    setPrefs(p => ({ ...p, [key]: value }))
    // Persist to API using the snake_case field the backend allowlist expects.
    const token = (() => { try { const r = localStorage.getItem('scion-auth'); return r ? (JSON.parse(r)?.state?.token ?? null) : null } catch { return null } })()
    if (token) {
      fetch(`${import.meta.env.VITE_API_URL ?? 'http://localhost:8080'}/api/v1/me/preferences`, {
        method: 'PUT', headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ [PREF_API_KEY[key]]: value }),
      }).catch(() => {})
    }
  }, [])

  // ARIA live region announcer
  const announce = useCallback((message: string, priority: 'polite' | 'assertive' = 'polite') => {
    if (priority === 'assertive') {
      setAssertiveMsg('')
      setTimeout(() => setAssertiveMsg(message), 100)
    } else {
      setPoliteMsg('')
      setTimeout(() => setPoliteMsg(message), 100)
    }
  }, [])

  return (
    <AccessibilityContext.Provider value={{ prefs, updatePref, announce }}>
      {/* Skip navigation link — WCAG 2.4.1 */}
      <a
        href="#main-content"
        style={{
          // Hide via transform (translateY), NOT top:-40 — the global WCAG rule
          // `a { min-height: 44px }` makes this link 44px tall, so top:-40 left a
          // ~4px sliver of its white border peeking at the top-left (looked like a
          // stray white bar above the logo). translateY(-120%) hides it fully
          // regardless of height; focus brings it back for keyboard users.
          position: 'fixed', top: 0, left: 0, zIndex: 9999,
          background: 'var(--color-background-primary)',
          color: 'var(--color-text-primary)',
          padding: '8px 16px', fontWeight: 500, fontSize: 14,
          border: '2px solid var(--color-text-primary)',
          textDecoration: 'none',
          transform: 'translateY(-120%)',
          transition: 'transform 0.12s ease',
        }}
        onFocus={e => { (e.currentTarget as HTMLAnchorElement).style.transform = 'translateY(0)' }}
        onBlur={e => { (e.currentTarget as HTMLAnchorElement).style.transform = 'translateY(-120%)' }}
      >
        Skip to main content
      </a>

      {/* ARIA live regions — screen reader announcements */}
      <div
        role="status"
        aria-live="polite"
        aria-atomic="true"
        style={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden', clip: 'rect(0,0,0,0)', whiteSpace: 'nowrap' }}
      >
        {politeMsg}
      </div>
      <div
        role="alert"
        aria-live="assertive"
        aria-atomic="true"
        style={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden', clip: 'rect(0,0,0,0)', whiteSpace: 'nowrap' }}
      >
        {assertiveMsg}
      </div>

      {children}
    </AccessibilityContext.Provider>
  )
}

export const useAccessibility = () => useContext(AccessibilityContext)

// Chart colours for Recharts/SVG, which CANNOT use CSS var() in fill/stroke/tick
// attributes. Returns real hex, reactive to light/dark mode — mirrors the
// surface/text palette in design-system.css. Use at chart colour-prop sites:
//   const chartC = useChartColors()  →  tick={{ fill: chartC.muted }} stroke={chartC.grid}
export function useChartColors() {
  const { prefs } = useAccessibility()
  const light = prefs.mode === 'light'
  // Recharts/SVG can't use var() in fill/stroke, so resolve the active accent
  // theme's CSS vars to real hex here. Re-resolves whenever the component
  // re-renders (which it does on a prefs.theme/mode change via context).
  const resolve = (name: string, fallback: string) => {
    if (typeof document === 'undefined') return fallback
    const v = getComputedStyle(document.documentElement).getPropertyValue(name).trim()
    return v || fallback
  }
  return {
    text:  light ? '#1B1F15' : '#F0EDE4',
    muted: light ? '#565248' : '#A8A49C',
    faint: light ? '#87837A' : '#6B6760',
    grid:  light ? 'rgba(0,0,0,0.10)' : 'rgba(255,255,255,0.08)',
    axis:  light ? 'rgba(0,0,0,0.22)' : 'rgba(255,255,255,0.16)',
    accent:  resolve('--color-primary', '#F5A623'), // themed accent as hex (charts)
    accent2: resolve('--color-accent', '#2D6A2F'),  // themed secondary accent as hex
  }
}

// ── Focus management hooks ────────────────────────────────────────────────────

/**
 * useFocusTrap: traps keyboard focus within a container (modal, drawer, dropdown).
 * Cycles forward with Tab and backward with Shift+Tab.
 * Returns a ref to attach to the container element.
 */
export function useFocusTrap(active: boolean) {
  const containerRef = useRef<HTMLElement | null>(null)

  useEffect(() => {
    if (!active || !containerRef.current) return
    const container = containerRef.current

    const focusable = 'a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])'

    // Focus first focusable element on activation
    const first = container.querySelectorAll<HTMLElement>(focusable)[0]
    first?.focus()

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Tab') return
      const elements = Array.from(container.querySelectorAll<HTMLElement>(focusable)).filter(el => !el.closest('[aria-hidden="true"]'))
      if (elements.length === 0) return
      const firstEl = elements[0]
      const lastEl = elements[elements.length - 1]
      if (e.shiftKey) {
        if (document.activeElement === firstEl) { e.preventDefault(); lastEl.focus() }
      } else {
        if (document.activeElement === lastEl) { e.preventDefault(); firstEl.focus() }
      }
    }

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') container.dispatchEvent(new CustomEvent('grafty:escape'))
    }

    document.addEventListener('keydown', handleKeyDown)
    document.addEventListener('keydown', handleEscape)
    return () => {
      document.removeEventListener('keydown', handleKeyDown)
      document.removeEventListener('keydown', handleEscape)
    }
  }, [active])

  return containerRef
}

/**
 * useFocusRestore: restores focus to the trigger element when a modal/panel closes.
 */
export function useFocusRestore(isOpen: boolean) {
  const triggerRef = useRef<HTMLElement | null>(null)

  useEffect(() => {
    if (isOpen) {
      triggerRef.current = document.activeElement as HTMLElement
    } else {
      triggerRef.current?.focus()
    }
  }, [isOpen])

  return triggerRef
}

/**
 * useRovingTabIndex: implements roving tabindex for a list/menu.
 * Arrow keys move focus; only one item is in the tab order at a time.
 */
export function useRovingTabIndex(count: number, orientation: 'horizontal' | 'vertical' = 'vertical') {
  const [activeIndex, setActiveIndex] = useState(0)

  const handleKeyDown = useCallback((e: React.KeyboardEvent, _index: number) => {
    const prevKey = orientation === 'vertical' ? 'ArrowUp' : 'ArrowLeft'
    const nextKey = orientation === 'vertical' ? 'ArrowDown' : 'ArrowRight'

    if (e.key === nextKey) {
      e.preventDefault()
      setActiveIndex(i => (i + 1) % count)
    } else if (e.key === prevKey) {
      e.preventDefault()
      setActiveIndex(i => (i - 1 + count) % count)
    } else if (e.key === 'Home') {
      e.preventDefault(); setActiveIndex(0)
    } else if (e.key === 'End') {
      e.preventDefault(); setActiveIndex(count - 1)
    }
  }, [count, orientation])

  return { activeIndex, setActiveIndex, handleKeyDown,
    getTabIndex: (index: number) => index === activeIndex ? 0 : -1 }
}

// ── Form accessibility helpers ─────────────────────────────────────────────────

/**
 * useFormField: generates ARIA IDs and error association for a form field.
 * Usage: const { fieldId, errorId, describeId, ariaProps } = useFormField('email')
 */
export function useFormField(name: string, error?: string, description?: string) {
  const fieldId = `field-${name}`
  const errorId = `error-${name}`
  const describeId = `desc-${name}`

  const ariaProps = {
    id: fieldId,
    'aria-invalid': error ? 'true' as const : undefined,
    'aria-describedby': [error && errorId, description && describeId].filter(Boolean).join(' ') || undefined,
    'aria-required': true as const,
  }

  return { fieldId, errorId, describeId, ariaProps }
}

// ── Accessible button component ───────────────────────────────────────────────

interface AccessibleButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  loading?: boolean
  loadingLabel?: string
  variant?: 'primary' | 'secondary' | 'danger' | 'ghost'
}

export function AccessibleButton({
  children, loading, loadingLabel = 'Loading...', disabled, variant = 'secondary',
  style, ...props
}: AccessibleButtonProps) {
  const variantStyles: Record<string, React.CSSProperties> = {
    primary:   { background: '#2D6A2F', color: '#fff', border: 'none' },
    secondary: { background: 'var(--color-background-secondary)', color: 'var(--color-text-primary)', border: '0.5px solid var(--color-border-secondary)' },
    danger:    { background: '#A32D2D', color: '#fff', border: 'none' },
    ghost:     { background: 'none', color: 'var(--color-text-secondary)', border: 'none' },
  }

  return (
    <button
      {...props}
      disabled={disabled || loading}
      aria-disabled={disabled || loading}
      aria-busy={loading}
      style={{
        ...variantStyles[variant],
        borderRadius: 8, padding: '8px 16px', fontSize: 14,
        cursor: disabled || loading ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.55 : 1,
        // High contrast focus ring — WCAG 2.4.7
        outline: 'none',
        ...style,
      }}
      // Focus ring applied via CSS (not inline) to support :focus-visible
    >
      {loading ? (
        <>
          <span aria-hidden="true" style={{ marginRight: 6 }}>⋯</span>
          <span className="sr-only">{loadingLabel}</span>
        </>
      ) : children}
    </button>
  )
}

// ── Accessible form field wrapper ─────────────────────────────────────────────

interface FieldProps {
  label: string
  name: string
  error?: string
  hint?: string
  required?: boolean
  children: (ariaProps: ReturnType<typeof useFormField>['ariaProps']) => ReactNode
}

export function Field({ label, name, error, hint, required = false, children }: FieldProps) {
  const { fieldId, errorId, describeId, ariaProps } = useFormField(name, error, hint)
  return (
    <div style={{ marginBottom: 16 }}>
      <label
        htmlFor={fieldId}
        style={{ display: 'block', fontSize: 13, color: 'var(--color-text-secondary)', marginBottom: 5, fontWeight: 500 }}
      >
        {label}
        {required && <span aria-label="required" style={{ color: '#A32D2D', marginLeft: 3 }}>*</span>}
      </label>
      {hint && (
        <p id={describeId} style={{ fontSize: 12, color: 'var(--color-text-tertiary)', margin: '0 0 5px' }}>
          {hint}
        </p>
      )}
      {children(ariaProps)}
      {error && (
        <p id={errorId} role="alert" style={{ fontSize: 12, color: 'var(--color-text-danger)', marginTop: 5, display: 'flex', alignItems: 'center', gap: 4 }}>
          <span aria-hidden="true">⚠</span> {error}
        </p>
      )}
    </div>
  )
}

// ── Screen reader only text ────────────────────────────────────────────────────

export function SrOnly({ children }: { children: ReactNode }) {
  return (
    <span style={{
      position: 'absolute', width: 1, height: 1, padding: 0, margin: -1,
      overflow: 'hidden', clip: 'rect(0,0,0,0)', whiteSpace: 'nowrap', border: 0,
    }}>
      {children}
    </span>
  )
}

// ── Accessible dialog (modal) ─────────────────────────────────────────────────

interface DialogProps {
  isOpen: boolean
  onClose: () => void
  title: string
  description?: string
  children: ReactNode
}

export function AccessibleDialog({ isOpen, onClose, title, description, children }: DialogProps) {
  const trapRef = useFocusTrap(isOpen)
  useFocusRestore(isOpen)
  const titleId = `dialog-title-${title.replace(/\s/g, '-').toLowerCase()}`
  const descId = description ? `dialog-desc-${titleId}` : undefined

  useEffect(() => {
    if (!isOpen) return
    const handleEsc = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    document.addEventListener('keydown', handleEsc)
    return () => document.removeEventListener('keydown', handleEsc)
  }, [isOpen, onClose])

  if (!isOpen) return null

  return (
    <div
      role="presentation"
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 50, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      onClick={e => { if (e.target === e.currentTarget) onClose() }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={descId}
        ref={trapRef as React.RefObject<HTMLDivElement>}
        style={{ background: 'var(--color-background-primary)', borderRadius: 16, padding: 28, width: '90%', maxWidth: 480, border: '0.5px solid var(--color-border-secondary)' }}
      >
        <h2 id={titleId} style={{ fontSize: 18, fontWeight: 500, margin: '0 0 4px' }}>{title}</h2>
        {description && <p id={descId} style={{ fontSize: 13, color: 'var(--color-text-secondary)', margin: '0 0 20px' }}>{description}</p>}
        {children}
      </div>
    </div>
  )
}

// ── Accessible status badge ────────────────────────────────────────────────────

interface StatusBadgeProps {
  status: string
  label?: string // override display text (for SR)
}

// Don't convey meaning by color alone — WCAG 1.4.1
export function StatusBadge({ status, label }: StatusBadgeProps) {
  const config: Record<string, { bg: string; color: string; icon: string }> = {
    active:    { bg: '#EAF3DE', color: '#27500A', icon: '●' },
    suspended: { bg: '#FCEBEB', color: '#A32D2D', icon: '⊘' },
    pending:   { bg: '#FAEEDA', color: '#633806', icon: '◐' },
    cancelled: { bg: '#FCEBEB', color: '#A32D2D', icon: '×' },
    paid:      { bg: '#EAF3DE', color: '#27500A', icon: '✓' },
    overdue:   { bg: '#FAEEDA', color: '#633806', icon: '!' },
  }
  const c = config[status] || { bg: '#F1EFE8', color: '#444441', icon: '○' }
  const displayText = label || status

  return (
    <span
      style={{ background: c.bg, color: c.color, fontSize: 11, fontWeight: 500, padding: '2px 8px', borderRadius: 99, display: 'inline-flex', alignItems: 'center', gap: 4 }}
      aria-label={`Status: ${displayText}`}
    >
      <span aria-hidden="true">{c.icon}</span>
      <span style={{ textTransform: 'capitalize' }}>{displayText}</span>
    </span>
  )
}

// ── Global ADA CSS (inject into design-system.css) ───────────────────────────
// This documents the CSS additions needed — actual styles go in design-system.css

export const ADA_CSS_ADDITIONS = `
/* ── WCAG Focus indicators (2.4.7, 2.4.11) ────────────────────────── */
/* Focus ring visible in all input modes when .focus-visible-always is set */
:focus-visible,
.focus-visible-always :focus {
  outline: 3px solid var(--color-primary);   /* mango amber — high contrast on dark bg */
  outline-offset: 2px;
  border-radius: 4px;
}

/* Skip link */
a[href="#main-content"]:focus {
  top: 0 !important;
}

/* Screen reader only utility */
.sr-only {
  position: absolute; width: 1px; height: 1px; padding: 0; margin: -1px;
  overflow: hidden; clip: rect(0,0,0,0); white-space: nowrap; border: 0;
}

/* ── Motion (WCAG 2.3.3) ─────────────────────────────────────────── */
.reduce-motion *, .reduce-motion *::before, .reduce-motion *::after {
  animation-duration: 0.01ms !important;
  animation-iteration-count: 1 !important;
  transition-duration: 0.01ms !important;
}

/* ── Font size scaling (WCAG 1.4.4 — resize text) ───────────────── */
:root {
  --font-size-base: calc(16px + var(--font-size-offset, 0) * 2px);
}
body { font-size: var(--font-size-base); }

/* ── High contrast overrides ────────────────────────────────────── */
[data-high-contrast="true"] {
  --color-border-tertiary: rgba(255,255,255,0.4);
  --color-border-secondary: rgba(255,255,255,0.6);
  --color-text-secondary: var(--color-text-primary);
}

/* ── Interactive element minimum touch target (WCAG 2.5.5) ──────── */
button, a, [role="button"], input, select, textarea, [tabindex]:not([tabindex="-1"]) {
  min-height: 44px;
  min-width: 44px;
}
/* Override for inline/compact elements */
.compact-target { min-height: unset; min-width: unset; }

/* ── Error states must not rely on color alone (WCAG 1.4.1) ──────── */
[aria-invalid="true"] {
  border-color: var(--color-border-danger) !important;
  /* Shape indicator in addition to color: */
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16'%3E%3Ccircle cx='8' cy='8' r='6' fill='none' stroke='%23A32D2D' stroke-width='2'/%3E%3Ctext x='8' y='12' text-anchor='middle' fill='%23A32D2D' font-size='10'%3E!%3C/text%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 10px center;
  padding-right: 36px;
}

/* ── Color contrast — Grafty palette against dark background ─────── */
/* Verified against WCAG 1.4.3: all text ≥ 4.5:1 against #0E1209    */
/* Mango amber #F5A623 on #0E1209 = 8.2:1 ✓                          */
/* Grove green #2D6A2F on #FFFFFF = 5.1:1 ✓                          */
/* var(--color-text-secondary) on bg = verified per theme mode        */
`
